import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Form } from '@components/atoms/Form';
import { Button, ButtonMode } from '@components/atoms/Button';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { InputErrors } from '@types';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { useCreateStore } from '@services/useCreateStore';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useNotifications } from '@hooks/useNotifications';
import { useGetCaes } from '@services/useGetCaes';
import { useUpdateLogin } from '@services/useUpdateLogin';
import MultipleSelectWithSearch from '@components/atoms/MultipleSelectWithSearch/MultipleSelectWithSearch';

interface StoreFormErrors {
    general: string;
    name: InputErrors;
    caes: InputErrors;
    address: InputErrors;
    fiscalNumber: InputErrors;
    email: InputErrors;
    postalCode: InputErrors;
    city: InputErrors;
    country: InputErrors;
    latitude: InputErrors;
    longitude: InputErrors;
    phoneNumber: InputErrors;
    type: InputErrors;
    url: InputErrors;
}

export const MyCompanyCreate: FC = () => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { request: requestAllCaes } = useGetCaes();
    const { login, createdStore, isLoadingAllCaes, allCaes, allSettings } = useData();
    const { request: requestUpdateLogin } = useUpdateLogin();
    const {
        request: requestCreateStore,
        loading: loadingCreateStore,
        errorMessage,
        validationErrors,
        success: successMessage,
        lastResponseTime: lastCreate,
    } = useCreateStore();
    const { t } = useTranslation();

    const translations = {
        title: t('register-company-component.title'),
        nameLabel: t('register-company-component.name.label'),
        caeLabel: t('register-company-component.cae.label'),
        addressLabel: t('register-company-component.address.label'),
        addressRequiredError: t('register-company-component.address.required-error'),
        fiscalNumberLabel: t('register-company-component.fiscal-number.label'),
        emailLabel: t('register-company-component.email.label'),
        postalCodeLabel: t('register-company-component.postal-code.label'),
        postalCodeRequiredError: t('register-company-component.postal-code.required-error'),
        cityLabel: t('register-company-component.city.label'),
        cityRequiredError: t('register-company-component.city.required-error'),
        countryLabel: t('register-company-component.country.label'),
        countryRequiredError: t('register-company-component.country.required-error'),
        latitudeLabel: t('register-company-component.latitude.label'),
        longitudeLabel: t('register-company-component.longitude.label'),
        phoneNumberLabel: t('register-company-component.phone-number.label'),
        typeLabel: t('register-company-component.type.label'),
        urlLabel: t('register-company-component.url.label'),
        caeRequiredError: t('register-company-component.cae.required-error'),
        nameRequiredError: t('register-company-component.name.required-error'),
        emailInvalidError: t('register-company-component.email.invalid-error'),
        typeRequiredError: t('register-company-component.type.required-error'),
        submitButton: t('register-company-component.submit.button'),
        default: t('select-default-option'),
        storeTypeProduct: t('store-type.product'),
        storeTypeService: t('store-type.service'),
        storeTypeBoth: t('store-type.both'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [caes, setCaes] = useState<{ uuid?: string; cae?: string; description?: string }[] | []>([]);
    const [address, setAddress] = useState<string>('');
    const [fiscalNumber, setFiscalNumber] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [type, setType] = useState<string>('');
    const [url, setUrl] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<StoreFormErrors>({
        general: '',
        name: [],
        caes: [],
        address: [],
        fiscalNumber: [],
        email: [],
        postalCode: [],
        city: [],
        country: [],
        latitude: [],
        longitude: [],
        phoneNumber: [],
        type: [],
        url: [],
    });

    const handleOnClickSaveStore = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.caes.length === 0 &&
            errors.address.length === 0 &&
            errors.fiscalNumber.length === 0 &&
            errors.email.length === 0 &&
            errors.postalCode.length === 0 &&
            errors.city.length === 0 &&
            errors.country.length === 0 &&
            errors.latitude.length === 0 &&
            errors.longitude.length === 0 &&
            errors.phoneNumber.length === 0 &&
            errors.type.length === 0 &&
            errors.url.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => StoreFormErrors = () => {
        const newErrors: StoreFormErrors = {
            general: '',
            name: [],
            caes: [],
            address: [],
            fiscalNumber: [],
            email: [],
            postalCode: [],
            city: [],
            country: [],
            latitude: [],
            longitude: [],
            phoneNumber: [],
            type: [],
            url: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !address.trim() && newErrors.address.push(translations.addressRequiredError);
        !postalCode.trim() && newErrors.postalCode.push(translations.postalCodeRequiredError);
        !city.trim() && newErrors.city.push(translations.cityRequiredError);
        !country.trim() && newErrors.country.push(translations.countryRequiredError);
        // check if email is valid
        email.trim() && !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        !type && newErrors.type.push(translations.typeRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        requestCreateStore({
            commercial_name: name,
            caes: caes,
            address: address,
            fiscal_number: fiscalNumber,
            email: email,
            postal_code: postalCode,
            city: city,
            country: country,
            latitude: latitude !== null ? latitude : undefined,
            longitude: longitude !== null ? longitude : undefined,
            phone_number: phoneNumber,
            type: type,
            url: url,
        });
    };

    // if create service returned successfully, dispatch notification and go to product's edit page
    // if not, show errors
    useEffect(() => {
        if (lastCreate) {
            if (errorMessage || validationErrors) {
                setErrors({
                    general: errorMessage as string,
                    // match form request errors with frontend errors object
                    name: validationErrors?.commercial_name || [],
                    caes: validationErrors?.caes || [],
                    address: validationErrors?.address || [],
                    fiscalNumber: validationErrors?.fiscal_number || [],
                    email: validationErrors?.email || [],
                    postalCode: validationErrors?.postal_code || [],
                    city: validationErrors?.city || [],
                    country: validationErrors?.country || [],
                    latitude: validationErrors?.latitude || [],
                    longitude: validationErrors?.longitude || [],
                    phoneNumber: validationErrors?.phone_number || [],
                    type: validationErrors?.type || [],
                    url: validationErrors?.url || [],
                });
            } else if (login && createdStore !== null) {
                showDefaultNotification({ type: 'success', message: successMessage || '' });
                requestUpdateLogin();

                if (type !== 'product' && allSettings?.data?.module_services?.schedule_by_employee) {
                    navigate(`/${SITE_URLS.myWorkers}`);
                } else {
                    navigate(`/${SITE_URLS.myCompanies}`);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdStore]);

    useEffect(() => {
        !isLoadingAllCaes && requestAllCaes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCAEsChange = (selectedUuids: string[]) => {
        const updatedCaes = allCaes?.data.filter((cae) => selectedUuids.includes(cae.uuid ?? ''));
        setCaes(updatedCaes ?? []);
    };

    return (
        <MyAccountContainer title={translations.title}>
            <div className="pt-4">
                <Form formRef={formRef} onSubmit={() => submitForm()}>
                    <>
                        {errors.general && (
                            <>
                                <ErrorMessages messages={[errors.general]} />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </>
                        )}
                    </>

                    <div className="row">
                        {/* name */}
                        <div className="col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={`${translations.nameLabel}*`}
                                value={name}
                                onChange={(value) => setName(value)}
                                disabled={loadingCreateStore}
                                state={errors.name.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.name}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>

                        {/* CAE */}
                        <div className="col-12 mb-4">
                            <MultipleSelectWithSearch
                                label={`${translations.caeLabel}*`}
                                options={
                                    allCaes?.data?.map((cae) => ({
                                        value: cae.uuid || '',
                                        label: `${cae.cae} - ${cae.description}`,
                                    })) ?? []
                                }
                                value={caes.map((cae) => cae.uuid || '')}
                                onChange={handleCAEsChange}
                            />
                            {errors.caes.length > 0 && <ErrorMessages messages={errors.caes} />}
                        </div>

                        {/* address */}
                        <div className="col-sm-7 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={`${translations.addressLabel}*`}
                                value={address}
                                onChange={(value) => setAddress(value)}
                                disabled={loadingCreateStore}
                                state={errors.address.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.address}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* fiscal number */}
                        <div className="col-sm-5 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.fiscalNumberLabel}
                                value={fiscalNumber}
                                onChange={(value) => setFiscalNumber(value)}
                                disabled={loadingCreateStore}
                                state={errors.fiscalNumber.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.fiscalNumber}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* email */}
                        <div className="col-xl-5 col-md-6 col-12">
                            <Input
                                type={InputTypes.email}
                                variant={InputVariants.labeledInput}
                                label={translations.emailLabel}
                                value={email}
                                onChange={(value) => setEmail(value)}
                                state={errors.email.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.email}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* postal code */}
                        <div className="col-xl-2 col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                label={`${translations.postalCodeLabel}*`}
                                variant={InputVariants.labeledInput}
                                value={postalCode}
                                onChange={(value) => setPostalCode(value)}
                                maxLength={8}
                                state={errors.postalCode.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.postalCode}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* city */}
                        <div className="col-xl-2 col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={`${translations.cityLabel}*`}
                                value={city}
                                onChange={(value) => setCity(value)}
                                disabled={loadingCreateStore}
                                state={errors.city.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.city}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* country */}
                        <div className="col-xl-3 col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={`${translations.countryLabel}*`}
                                value={country}
                                onChange={(value) => setCountry(value)}
                                disabled={loadingCreateStore}
                                state={errors.country.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.country}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* latitude */}
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.number}
                                variant={InputVariants.labeledInput}
                                label={translations.latitudeLabel}
                                value={latitude !== null ? String(latitude) : ''}
                                onChange={(value) => setLatitude(value)}
                                disabled={loadingCreateStore}
                                state={errors.latitude.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.latitude}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* longitude */}
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.longitudeLabel}
                                value={longitude !== null ? String(longitude) : ''}
                                onChange={(value) => setLongitude(value)}
                                disabled={loadingCreateStore}
                                state={errors.longitude.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.longitude}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>

                        {/* phone number */}
                        <div className="col-sm-6 col-12 pt-sm-2 mt-sm-1">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.phoneNumberLabel}
                                value={phoneNumber}
                                onChange={(value) => setPhoneNumber(value)}
                                disabled={loadingCreateStore}
                                state={errors.phoneNumber.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.phoneNumber}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* type */}
                        <div className="col-sm-6 col-12">
                            <div>
                                <Typography color="muted" fontSize="12">
                                    <>{translations.typeLabel}*</>
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    disabled={loadingCreateStore}
                                    options={[
                                        { value: '', text: translations.default },
                                        { value: 'product', text: translations.storeTypeProduct },
                                        { value: 'service', text: translations.storeTypeService },
                                        { value: 'both', text: translations.storeTypeBoth },
                                    ]}
                                    value={type || undefined}
                                    onChange={(value) => setType(value)}
                                />
                                {errors.type.length > 0 && <ErrorMessages messages={errors.type} />}
                            </div>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        {/* url */}
                        <div className="col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.urlLabel}
                                value={url}
                                onChange={(value) => setUrl(value)}
                                disabled={loadingCreateStore}
                                state={errors.url.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.url}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                    </div>

                    {/* submit */}
                    <div className="col-12 text-end">
                        <Button
                            icon="fa-solid fa-floppy-disk"
                            label={translations.submitButton}
                            mode={ButtonMode.primary}
                            disabled={loadingCreateStore}
                            onClick={() => handleOnClickSaveStore()}
                        />
                    </div>
                </Form>
            </div>
        </MyAccountContainer>
    );
};
